import { useEffect, useRef, useState } from 'react';

export function useMicrophoneLevel(deviceId: string | undefined) {
  const [level, setLevel] = useState(0);
  const animFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);

  const start = async () => {
    stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId && deviceId !== 'default' ? { deviceId: { exact: deviceId } } : true
      });
      streamRef.current = stream;
      const ctx = new AudioContext();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        // getByteFrequencyData returns 0-255, normalizing for adequate representation inside vu-meter-bar
        setLevel(Math.min(100, (avg / 128) * 100 * 2));
        animFrameRef.current = requestAnimationFrame(tick);
      };
      animFrameRef.current = requestAnimationFrame(tick);
    } catch {
      setLevel(0);
    }
  };

  const stop = () => {
    if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    void ctxRef.current?.close();
    ctxRef.current = null;
    streamRef.current = null;
    setLevel(0);
  };

  // stopping on unmount
  useEffect(() => () => stop(), []);

  return { level, start, stop };
}

// setSinkId is not part of the official TypeScript type definitions for AudioContext
// it's a relatively new browser API that the @types/web package hasn't fully caught up with
interface AudioContextWithSink extends AudioContext {
  setSinkId(sinkId: string): Promise<void>;
}

export const playTestSound = async (deviceId = 'default') => {
  const ctx = new AudioContext() as AudioContextWithSink;

  if (deviceId !== 'default' && ctx.setSinkId) {
    try {
      await ctx.setSinkId(deviceId);
    } catch (e) {
      console.warn('Failed to set sink ID:', e);
    }
  }

  // Convolution reverb using a synthesized impulse response
  const makeReverb = (duration = 1.5, decay = 2.5) => {
    const sampleRate = ctx.sampleRate;
    const length = sampleRate * duration;
    const impulse = ctx.createBuffer(2, length, sampleRate);
    for (let c = 0; c < 2; c++) {
      const data = impulse.getChannelData(c);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
    }
    const reverb = ctx.createConvolver();
    reverb.buffer = impulse;
    return reverb;
  };

  const reverb = makeReverb();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.6;
  reverb.connect(masterGain);
  masterGain.connect(ctx.destination);

  const playNote = (freq: number, start: number, duration: number, volume = 0.18) => {
    // Two slightly detuned oscillators for a chorus/warmth effect
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = 'sine';
    osc2.type = 'triangle'; // adds subtle harmonic warmth
    osc1.frequency.setValueAtTime(freq, start);
    osc2.frequency.setValueAtTime(freq, start);
    osc2.detune.setValueAtTime(7, start); // gentle chorus

    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(volume, start + 0.08);      // soft attack
    gain.gain.setTargetAtTime(volume * 0.7, start + 0.08, 0.3);   // slight decay
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(reverb);
    gain.connect(ctx.destination); // dry signal too

    osc1.start(start);
    osc2.start(start);
    osc1.stop(start + duration + 0.1);
    osc2.stop(start + duration + 0.1);
  };

  // Cmaj7 arpeggio: C5 → E5 → G5 → B5
  const now = ctx.currentTime;
  playNote(523.25, now, 1.2);  // C5
  playNote(659.25, now + 0.12, 1.1);  // E5
  playNote(783.99, now + 0.24, 1.0);  // G5
  playNote(987.77, now + 0.36, 1.4);  // B5

  setTimeout(() => void ctx.close(), 3000);
};
